import React from 'react';
import {
  Box,
  Typography,
  Container,
  Grid,
  Card,
  CardContent,
  Paper,
  Divider,
} from '@mui/material';
import { useAuth } from '../../Commun/contexts/AuthContext';
import Logo from '../../Commun/components/Logo';
import {
  Speed as KpiIcon,
  Timeline as StatsIcon,
  Security as SecurityIcon,
  Assessment as ReportIcon
} from '@mui/icons-material';

const DashboardPage: React.FC = () => {
  const { user } = useAuth();
  const isAdmin = user?.isAdmin;

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Header Section */}
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Typography variant="h4" component="h1" sx={{ fontWeight: 700, color: 'text.primary', letterSpacing: '-0.02em' }}>
            Tableau de bord
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mt: 0.5 }}>
            Ravi de vous revoir, <Box component="span" sx={{ fontWeight: 600, color: 'text.primary' }}>{user?.profileLib || user?.email || 'Utilisateur'}</Box>
          </Typography>
        </Box>
        <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
          <Typography variant="caption" sx={{
            bgcolor: 'background.paper',
            px: 2,
            py: 1,
            borderRadius: 2,
            boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
            fontWeight: 600,
            color: 'text.secondary',
            border: '1px solid',
            borderColor: 'divider'
          }}>
            {new Date().toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' })}
          </Typography>
        </Box>
      </Box>

      {/* General Stats - Visible to Everyone */}
      <Box sx={{ mb: 6 }}>
        <Typography variant="h5" sx={{ mb: 3, fontWeight: 600, display: 'flex', alignItems: 'center' }}>
          <TimelineIcon sx={{ mr: 1, color: 'info.main' }} /> Aperçu Général
        </Typography>
        <Grid container spacing={3}>
          <Grid item xs={12} md={4}>
            <WelcomeCard />
          </Grid>
          <Grid item xs={12} md={8}>
            {/* Placeholder for common stats or an image */}
            <Paper
              elevation={0}
              sx={{
                p: 3,
                height: '100%',
                bgcolor: 'background.paper',
                borderRadius: 4,
                border: '1px solid',
                borderColor: 'divider',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center'
              }}>
              <Typography variant="h6" color="text.secondary">Activité Récente</Typography>
              <Typography variant="body2" color="text.disabled">Aucune nouvelle notification pour le moment.</Typography>
            </Paper>
          </Grid>
        </Grid>
      </Box>

      {/* Admin Section - Protected */}
      {isAdmin && (
        <Box sx={{ animation: 'slideUp 0.6s ease-out' }}>
          <Divider sx={{ mb: 4 }} />
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
            <SecurityIcon sx={{ mr: 1, color: 'error.main', fontSize: 32 }} />
            <Typography variant="h4" sx={{ fontWeight: 700, color: 'text.primary' }}>
              Zone Administrative
            </Typography>
          </Box>

          {/* Admin KPIs */}
          <Grid container spacing={4} sx={{ mb: 4 }}>
            <Grid item xs={12} sm={6} md={3}>
              <StatCard
                title="Revenu Total"
                value="1,245,000 €"
                icon={<KpiIcon />}
                color="#d32f2f"
                trend="+12%"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <StatCard
                title="Nouveaux Utilisateurs"
                value="342"
                icon={<StatsIcon />}
                color="#ed6c02"
                trend="+5%"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <StatCard
                title="Rapports Sensibles"
                value="12"
                icon={<ReportIcon />}
                color="#2e7d32"
                subtext="Nécessite révision"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <StatCard
                title="Performance Système"
                value="99.9%"
                icon={<KpiIcon />}
                color="#0288d1"
              />
            </Grid>
          </Grid>

          {/* Admin Detailed Reports */}
          <Grid container spacing={4}>
            <Grid item xs={12} lg={8}>
              <Paper sx={{ p: 3, borderRadius: 4, boxShadow: '0 4px 20px 0 rgba(0,0,0,0.05)' }}>
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>Rapports Financiers & Stratégiques</Typography>
                <Box sx={{ p: 2, bgcolor: 'background.default', borderRadius: 2 }}>
                  <Typography variant="body2" color="text.secondary">Graphique ou tableau détaillé ici...</Typography>
                </Box>
              </Paper>
            </Grid>
            <Grid item xs={12} lg={4}>
              <Paper sx={{ p: 3, borderRadius: 4, height: '100%', boxShadow: '0 4px 20px 0 rgba(0,0,0,0.05)' }}>
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>Actions Rapides</Typography>
                {/* Mock Actions */}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                  <AdminActionButton label="Générer Rapport Mensuel" />
                  <AdminActionButton label="Audit de Sécurité" />
                  <AdminActionButton label="Gestion des Utilisateurs" />
                </Box>
              </Paper>
            </Grid>
          </Grid>
        </Box>
      )}
    </Container>
  );
};

// --- Sub-components for cleaner code ---

const TimelineIcon = (props: any) => (
  <StatsIcon {...props} /> // Reusing Icon for now
);

const WelcomeCard = () => (
  <Card sx={{
    height: '100%',
    borderRadius: 4,
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    color: 'white',
    boxShadow: '0 10px 20px -5px rgba(118, 75, 162, 0.4)'
  }}>
    <CardContent sx={{ p: 4 }}>
      <Typography variant="h5" sx={{ mb: 1, fontWeight: 700 }}>Bonne journée !</Typography>
      <Typography variant="body1" sx={{ opacity: 0.9 }}>
        Consultez vos tâches et restez à jour avec les dernières notifications.
      </Typography>
    </CardContent>
  </Card>
);

const StatCard = ({ title, value, icon, color, trend, subtext }: any) => (
  <Paper sx={{
    p: 3,
    borderRadius: 4,
    height: '100%',
    position: 'relative',
    overflow: 'hidden',
    transition: 'transform 0.2s',
    '&:hover': { transform: 'translateY(-5px)', boxShadow: '0 10px 30px -10px rgba(0,0,0,0.1)' }
  }}>
    <Box sx={{
      position: 'absolute',
      top: -10,
      right: -10,
      width: 80,
      height: 80,
      borderRadius: '50%',
      bgcolor: `${color}15`, // 15 = low opacity hex
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center'
    }}>
      {React.cloneElement(icon, { sx: { fontSize: 40, color: color, opacity: 0.5 } })}
    </Box>
    <Box sx={{ position: 'relative', zIndex: 1 }}>
      <Box sx={{ mb: 2, p: 1.5, borderRadius: 2, bgcolor: `${color}15`, width: 'fit-content' }}>
        {React.cloneElement(icon, { sx: { color: color } })}
      </Box>
      <Typography variant="h4" sx={{ fontWeight: 700, mb: 0.5 }}>{value}</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>{title}</Typography>
      {trend && (
        <Typography variant="caption" sx={{ color: 'success.main', fontWeight: 600, display: 'block', mt: 1 }}>
          {trend} vs le mois dernier
        </Typography>
      )}
      {subtext && (
        <Typography variant="caption" sx={{ color: 'warning.main', fontWeight: 600, display: 'block', mt: 1 }}>
          {subtext}
        </Typography>
      )}
    </Box>
  </Paper>
);

const AdminActionButton = ({ label }: { label: string }) => (
  <Box
    component="button"
    sx={{
      p: 1.5,
      width: '100%',
      textAlign: 'left',
      borderRadius: 2,
      border: 'none',
      bgcolor: 'action.hover',
      cursor: 'pointer',
      transition: 'background 0.2s',
      typography: 'body2',
      fontWeight: 500,
      '&:hover': { bgcolor: 'action.selected' }
    }}
  >
    {label}
  </Box>
);

export default DashboardPage;
